import { IMessageListRuleTag } from "./MessagingListRuleQueryBuilder";
import { Select, Text } from "@radix-ui/themes";
import useTheme from "../../radix/useTheme";
import WatsonApi from "../../../backends/WatsonApi";
import { useStores } from "../../../stores/useStores";
import { MessagingListQueryOptions } from "./MessagingListRules";
import { RuleOrganizationSearch } from "./RuleOrganizationSearch";
import { IMessagingListModalIntent } from "./AddOrEditMessagingListModal";
import { useState } from "react";
import useAsyncEffect from "../../../hooks/useAsyncEffect";

export function RuleOrganizationDropdown(props: {
  organization: IMessageListRuleTag | null;
  setOrganization: React.Dispatch<
    React.SetStateAction<IMessageListRuleTag | null>
  >;
  modalIntent: IMessagingListModalIntent;
}) {
  const { organizationStore } = useStores();
  const radixTheme = useTheme();
  const [loadingOrgSuggestions, setLoadingOrgSuggestions] = useState(true);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);
  const [orgSuggestions, setOrgSuggestions] = useState<IMessageListRuleTag[]>(
    [],
  );
  const [searchResultOrgs, setSearchResultOrgs] = useState<
    IMessageListRuleTag[]
  >([]);
  const [searchValue, setSearchValue] = useState("");

  useAsyncEffect(async () => {
    const client = await WatsonApi();
    const response = await client.apis.organizations.organizations_list({
      organization_pk: organizationStore.organization.id,
      path__descendant_or_eq: organizationStore.organization.path,
      with_role: true,
      deleted: "false",
      ordering: "path",
      fields: ["title", "id", "type"],
      page_size: 8,
    });
    const results = JSON.parse(response.data).data;
    const options = results.map((org) => ({
      value: `${org.title} (${org.type})`,
      type: MessagingListQueryOptions.Organization,
      filter: org.id,
    }));
    if (
      props.modalIntent === "createList" &&
      options.findIndex(
        (o) => o.filter === organizationStore.currentOrganization.id,
      ) === -1
    ) {
      // if we're creating/editing a list - meaning we're not just on the messaging page - and the current org isn't in the suggestions list, add it
      options.splice(0, 0, {
        value: `${organizationStore.currentOrganization.title} (${organizationStore.currentOrganization.type})`,
        type: MessagingListQueryOptions.Organization,
        filter: organizationStore.currentOrganization.id,
      });
    }
    setOrgSuggestions(options);
    setLoadingOrgSuggestions(false);
  }, []);

  let content;
  if (loadingOrgSuggestions) {
    content = <Text>Loading...</Text>;
  } else {
    let subcontent;
    if (searchValue && !searchResultsLoading && searchResultOrgs.length === 0) {
      subcontent = <Text>No results found</Text>;
    } else {
      // This hides items rather than not rendering them because you can still have a search result selected even if
      // search results have been cleared, and Select has to maintain a reference to the item.
      subcontent = (
        <>
          {orgSuggestions.map((org: IMessageListRuleTag) => (
            <Select.Item
              style={{ display: searchValue ? "none" : undefined }}
              key={org.filter}
              value={JSON.stringify(org)}
            >
              {org.value}
            </Select.Item>
          ))}
          {searchResultOrgs.map((org: IMessageListRuleTag) => (
            <Select.Item
              style={{ display: searchValue ? undefined : "none" }}
              key={org.filter}
              value={JSON.stringify(org)}
            >
              {org.value}
            </Select.Item>
          ))}
        </>
      );
    }
    content = (
      <>
        <RuleOrganizationSearch
          setOrganizations={setSearchResultOrgs}
          modalIntent={props.modalIntent}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setSearchResultsLoading={setSearchResultsLoading}
          searchResultsLoading={searchResultsLoading}
          resetSearch={() => {
            setSearchValue("");
            setSearchResultsLoading(false);
          }}
        />
        {subcontent}
      </>
    );
  }

  return (
    <Select.Root
      value={
        props.organization ? JSON.stringify(props.organization) : undefined
      }
      onValueChange={(value) => props.setOrganization(JSON.parse(value))}
    >
      <Select.Trigger placeholder={"Select Organization"} />
      <Select.Content
        container={radixTheme.portalContainer?.current}
        position={"popper"}
      >
        {content}
      </Select.Content>
    </Select.Root>
  );
}
